import { BSON } from 'realm-web';
import { CUSTOMERS_COLLECTION, ORDERS_COLLECTION, REFUNDS_COLLECTION } from '@/constants/constants';
import { getCollection } from '@/utils';
import { FinancialGridResponse, Order, OrderFilter, SortOption } from '@/types/orders.type';
import { getDateRangeByFilter } from '@/services/Orders/helpers/getDateRangeByFilter';
import { getStagesByFilter } from './helpers/getGroupStageByFilter';
import { getDataWithEmptyDaysAndCorrectLabel } from './helpers/getDataWithEmptyDaysAndCorrectLabel';
import { performAggregation } from '@/utils/helpers/performAggregation';
import { buildMatchQuery } from './helpers/buildMatchQuery';

export const getOrdersByCompanyId = async (
  companyId: string,
  sort: SortOption = { createdAt: -1 },
  limit: number = 10,
  outlets: string[] = [],
): Promise<Order[] | undefined> => {
  try {
    const ordersCollection = getCollection(ORDERS_COLLECTION);
    const findQuery: any = {
      companyId: new BSON.ObjectID(companyId),
    };

    if (outlets.length > 0) {
      const outletsWithObjectIds = outlets.map((outletId) => new BSON.ObjectId(outletId));
      findQuery['posData'] = {
        $exists: true,
        $type: 'object',
      };
      findQuery['posData.outlet'] = {
        $in: outletsWithObjectIds,
      };
    }
    const orders = await ordersCollection?.find(findQuery, { sort, limit });
    if (!orders) {
      return [];
    }
    return BSON.EJSON.deserialize(orders) as Order[];
  } catch (err) {
    throw new Error('Error fetching orders');
  }
};

export const getSumByFilter = async (
  companyId: string,
  filter: OrderFilter,
  outlets: string[] = [],
) => {
  try {
    const ordersCollection = getCollection(ORDERS_COLLECTION);
    const query = getStagesByFilter(filter);

    const matchQuery = buildMatchQuery(companyId, filter, outlets);
    let aggregationPipeline: any[] = [
      {
        $match: matchQuery,
      },
    ];

    aggregationPipeline = [...aggregationPipeline, ...query];
    const data = await performAggregation(ordersCollection, aggregationPipeline);
    const dateWithEmptyDaysAndCorrectLabel = getDataWithEmptyDaysAndCorrectLabel(data, filter);

    return dateWithEmptyDaysAndCorrectLabel || [];
  } catch (err) {
    throw new Error('Error fetching sales data');
  }
};

export const getDataForItemGrid = async (
  companyId: string,
  filter: OrderFilter,
  outlets: string[] = [],
): Promise<FinancialGridResponse | undefined> => {
  try {
    const ordersCollection = getCollection(ORDERS_COLLECTION);
    const refundsCollection = getCollection(REFUNDS_COLLECTION);
    const { startDate, endDate } = getDateRangeByFilter(filter);
    const customersCollection = getCollection(CUSTOMERS_COLLECTION);

    const generalMatch: any = {
      companyId: new BSON.ObjectID(companyId),
      createdAt: {
        $gte: startDate,
        $lte: endDate,
      },
    };
    const orderMatch = { ...generalMatch };
    const refundsMatch = { ...generalMatch };
    if (outlets?.length > 0) {
      const outletsWithObjectIds = outlets.map((outletId) => new BSON.ObjectId(outletId));
      orderMatch['posData'] = {
        $exists: true,
        $type: 'object',
      };
      orderMatch['posData.outlet'] = {
        $in: outletsWithObjectIds,
      };
      refundsMatch['outletId'] = {
        $in: outletsWithObjectIds,
      };
    }
    const pipelineOrders = [
      {
        $match: orderMatch,
      },
      {
        $group: {
          _id: null,
          totalRevenue: { $sum: { $toDouble: '$summary.total' } },
          totalOrders: { $sum: 1 },
          totalDiscounts: {
            $sum: { $toDouble: '$summary.discountTotal' },
          },
        },
      },
      {
        $addFields: {
          averageOrderCost: {
            $cond: [
              { $gt: ['$totalOrders', 0] },
              { $divide: ['$totalRevenue', '$totalOrders'] },
              0,
            ],
          },
        },
      },
    ];

    const pipelineRefunds = [
      {
        $match: refundsMatch,
      },
      {
        $group: {
          _id: null,
          totalRefunds: { $sum: { $toDouble: '$summary.total' } },
        },
      },
    ];

    const pipelineCustomers = [
      {
        $match: {
          companyId: new BSON.ObjectID(companyId),
          createdAt: {
            $gte: startDate,
            $lte: endDate,
          },
        },
      },
      {
        $group: {
          _id: '$customerId',
        },
      },
      {
        $count: 'totalCustomers',
      },
    ];

    const [ordersData, refundsData, customersData] = await Promise.all([
      ordersCollection?.aggregate(pipelineOrders),
      refundsCollection?.aggregate(pipelineRefunds),
      customersCollection?.aggregate(pipelineCustomers),
    ]);
    const financialData: FinancialGridResponse = {
      ...ordersData[0],
      totalRefunds: refundsData[0]?.totalRefunds || 0,
      totalCustomers: customersData[0]?.totalCustomers || 0,
    };

    return financialData;
  } catch (err) {
    throw new Error('Error fetching data for finances grid');
  }
};

export const getTop5ProductsByFilter = async (
  companyId: string,
  filter: OrderFilter,
  outlets: string[] = [],
): Promise<
  | {
      productId: string;
      name: string;
      totalSales: number;
      quantitySold: number;
    }[]
  | undefined
> => {
  try {
    const ordersCollection = getCollection(ORDERS_COLLECTION);
    const matchQuery = buildMatchQuery(companyId, filter, outlets);
    const pipeline = [
      {
        $match: matchQuery,
      },
      {
        $unwind: '$lineItems',
      },
      {
        $group: {
          _id: '$lineItems.productId',
          name: { $first: '$lineItems.name' },
          totalSales: { $sum: { $toDouble: '$lineItems.total' } },
          quantitySold: { $sum: '$lineItems.quantity' },
        },
      },

      {
        $lookup: {
          from: 'products',
          localField: '_id',
          foreignField: '_id',
          as: 'productDetails',
        },
      },
      {
        $unwind: {
          path: '$productDetails',
          preserveNullAndEmptyArrays: false,
        },
      },
      {
        $project: {
          productId: '$_id',
          name: '$productDetails.name',
          sku: '$sku',
          totalSales: 1,
          quantitySold: 1,
          image: { $arrayElemAt: ['$productDetails.images', 0] },
        },
      },
      {
        $sort: { totalSales: -1 },
      },
      {
        $limit: 5,
      },
    ];
    const data = await performAggregation(ordersCollection, pipeline);
    if (data.length > 0) {
      return data.map((item) => ({
        productId: item._id,
        name: item.name,
        sku: item.sku,
        totalSales: item.totalSales,
        quantitySold: item.quantitySold,
        image: item.image,
      }));
    } else {
      return [];
    }
  } catch (err) {
    throw new Error('Error fetching top 5 products');
  }
};

export const getTop5CustomersByFilter = async (
  companyId: string,
  filter: OrderFilter,
  outlets: string[] = [],
): Promise<
  | {
      customerId: string;
      firstName: string;
      lastName: string;
      totalSpent: number;
      orderCount: number;
    }[]
  | undefined
> => {
  try {
    const ordersCollection = getCollection(ORDERS_COLLECTION);
    const matchQuery = buildMatchQuery(companyId, filter, outlets);
    const pipeline = [
      {
        $match: matchQuery,
      },
      {
        $group: {
          _id: '$customerId',
          totalSpent: { $sum: { $toDouble: '$summary.total' } },
          orderCount: { $sum: 1 },
        },
      },
      {
        $lookup: {
          from: 'customers',
          localField: '_id',
          foreignField: '_id',
          as: 'customerDetails',
        },
      },
      {
        $unwind: {
          path: '$customerDetails',
          preserveNullAndEmptyArrays: false,
        },
      },
      {
        $project: {
          customerId: '$_id',
          email: '$customerDetails.email',
          firstName: { $ifNull: ['$customerDetails.firstName', ''] },
          lastName: { $ifNull: ['$customerDetails.lastName', ''] },
          totalSpent: 1,
          orderCount: 1,
        },
      },
      {
        $sort: { totalSpent: -1 },
      },
      {
        $limit: 5,
      },
    ];

    const data = await performAggregation(ordersCollection, pipeline);
    if (data.length > 0) {
      return data.map((item) => ({
        customerId: item._id,
        firstName: item.firstName,
        lastName: item.lastName,
        totalSpent: item.totalSpent,
        orderCount: item.orderCount,
      }));
    } else {
      return [];
    }
  } catch (err) {
    throw new Error('Error fetching top 5 customers');
  }
};
